import * as transform from './transform';
import { isVerticalType, forAxisTicks, forAxisTicksReverse } from './util';
import { NBSP } from '../data/constants';
import { ANNOTATIONS_SPACE, ANNOTATIONS_SPACE_PRINT } from '../data/constants';
import * as element from '../util/element';


/**
 * Manipulates the SVG drawn by Highcharts to update the scale (Y) axis.
 */
export default function axesPostRenderYAxis(highchart, options, styles) {
  // The scale axis is always the Y axis, even on bar charts where the scale
  // axis visually is the X axis:
  // for wsj yaxis labels, set to tabular-nums
  const notVideoGroup = options.id !== 'video' && options.id !== 'twitterVideo';

  if (!options.print && options.product === 'wsj' && notVideoGroup) {
    highchart.container.getElementsByClassName('highcharts-yaxis-labels')[0].setAttribute('style', 'font-variant-numeric: tabular-nums;');
  }

  if (options.type === 'scatter' && options.headings.yaxisTitle.text) {
    moveYAxisTitle(highchart, options);
  }

  const axis = highchart.axes[1];
  if (isVerticalType(options.type)) {
    makeDecimalsConsistent(axis);
    extendTicks(axis, options);
    
  } else {
    if (options.type !== 'scatter') shiftLastHorizontalBarLine(highchart, options);
  }

  setZeroLineToBlack(axis, options, highchart);
  
  insertZeroesForEmptyBars(highchart.renderer, axis, options);
  if ((options.type === 'column' || options.type === 'bar') && (options.id !== 'video' && options.id !== 'twitterVideo')) {
    moveZeroLineToTop(highchart.container, axis, options);
  }
  if (notVideoGroup) {
    ensureLabelsFit(axis, options, highchart);
  }

  if (options.type === 'scatter' && (options.product === 'marketwatch' || options.product === 'financialnews')) {
    shiftYAxisLabelsScatter(options, highchart);
  }

  if (__WSJ__) {
    if (options.product === 'wsj' && options.type !== 'bar') {
      // moveTopLabel(axis, options);
    }
  }

  addSuffix(highchart.renderer, axis, options, styles, highchart);

  if (options.id === 'print-barrons') {
    if (axis.ticks[0]) setZeroLineWidth(axis.ticks[0], 1);
  }

  if(options.id === 'video' || options.id === 'twitterVideo'){
    if (axis.ticks[0]) {
      setZeroLineWidth(axis.ticks[0], options.id === 'video' ? 11 : 8);
      //sets xAxis line width to 1
      setXAxisLineToOne(highchart.axes[0].axisLine);
    }
}
}

function shiftYAxisLabelsScatter(options, highchart) {
  const container = highchart.container;
  const yAxisLabelsGroup = container.getElementsByClassName('highcharts-yaxis-labels')[0];
  let translateValue = 'translate(35, 0.5)';
  yAxisLabelsGroup.attributes.transform.nodeValue = translateValue;
}

function moveYAxisTitle(highchart, options) {
  const yAxisTitle = highchart.container.getElementsByClassName('highcharts-axis-title')[1];
  const yVal = Number(yAxisTitle.getAttribute('y'));
  if (options.product === 'wsj' || options.product === 'barrons') {
    yAxisTitle.setAttribute('y', yVal - (yVal * 0.015));
  }
}

function setZeroLineWidth(line, num) {
  line.gridLine.element.setAttribute('stroke-width', num);
}

function setXAxisLineToOne(line){
  line.element.setAttribute('stroke-width', 1);
}

// Set line representing the zero value to black.
function setZeroLineToBlack(axis, options, highchart) {
  let zeroTick = axis.ticks[0];

  if (__MARKETWATCH__) {
    if (options.product === 'marketwatch' && options.type === 'column') {
      return;
    }
  }

  let zeroY;
  if (options.type === 'scatter') {
    forAxisTicks(axis, (ticks, i) => {
      const label = ticks.label.textStr;
      if (label === '0' || label === '0<tspan fill-opacity="0">.0</tspan>') {
        zeroY = i;
      } 
    });
    
    if (zeroY !== undefined) {
      const zeroTickScatter = axis.ticks[zeroY];
      zeroTickScatter.gridLine.element.setAttribute('stroke', options.styles.axes.zeroGridLine.color || '#333');
    }
  }
  if (zeroTick && options.type !== 'scatter') {
    zeroTick.gridLine.element.setAttribute('stroke', '#000');

    if(__WSJ__){
      if(options.product === 'wsj'){
        zeroTick.gridLine.element.setAttribute('stroke', options.styles.axes.zeroGridLine.color);
      }
    }
  }
}
function makeDecimalsConsistent(axis) {
  const zeroIndex = axis.tickPositions.indexOf(0);
  const maxDecimals = getMaxTicksDecimals(axis);
  if (maxDecimals > 0) {
    forAxisTicks(axis, (tick, key, index) => {
      let textStr = tick.label.textStr;
      let indexDot = textStr.indexOf('.');
      let decimals = indexDot === -1 ? 0 : (textStr.length - indexDot - 1);
      if (key === 0) {
        textStr += '<tspan fill-opacity="0">';
      }
      if (indexDot === -1) {
        indexDot = textStr.length;
        textStr += '.';
      }
      while (decimals++ < maxDecimals) {
        textStr += '0';
      }
      if (key === 0) {
        textStr += '</tspan>';
      }
      tick.label.textStr = textStr;
      tick.label.element.innerHTML = textStr;
    });
  }
}

// Extend grid lines under the label text on the y-axis (for "vertical" axes).
function extendTicks(axis, options) {
  if (options.type === 'scatter' || (options.type === 'line' && options.product === 'marketwatch')) {
    let firstTick;
    let lastTick;
    forAxisTicks(axis, tick => {
      if (tick.isFirst) firstTick = tick;
      if (tick.isLast) lastTick = tick;
    });
    if (firstTick && lastTick) {
      const firstLine = firstTick.gridLine.element;
      const lastLine = lastTick.gridLine.element;
      const dFirst = transform.read(firstLine, 'd');
      const dLast = transform.read(lastLine, 'd');
      dFirst[0][0] = dFirst[0][0] - 1;
      dFirst[1][0] = dFirst[1][0] + 1;
      dLast[0][0] = dLast[0][0] - 1;
      dLast[1][0] = dLast[1][0] + 1;
      transform.write(firstLine, 'd', dFirst);
      transform.write(lastLine, 'd', dLast);
    }
    
  } else {
    forAxisTicks(axis, tick => {
      const line = tick.gridLine.element;
      if (line.hasAttribute('d')) {
        const d = transform.read(line, 'd');
        d[0][0] = 0;
        if (options.spaceAddedForAnnotations) {
          d[1][0] += options.print ? ANNOTATIONS_SPACE_PRINT : ANNOTATIONS_SPACE;
        }
        transform.write(line, 'd', d);
        // const d = line.getAttribute('d').split(/\s/g);
        // d[1] = 0;
        // if (options.spaceAddedForAnnotations) {
        //   console.log('???');
        // }
        // line.setAttribute('d', d.join(' '));
      }
    });
  }
  
}

function getMaxTicksDecimals(axis) {
  let maxDecimals = 0;
  forAxisTicks(axis, (tick, pos) => {
    const decimal = tick.label.textStr.split('.')[1];
    if (decimal !== undefined) {
      maxDecimals = Math.max(decimal.length, maxDecimals);
    }
  });
  return maxDecimals;
}

// Add units suffix string to the top tick label (`isLast`).
function addSuffix(renderer, axis, options, styles, highchart) {
  if (options.axes.units && isVerticalType(options.type)) {
    for (let key in axis.ticks) {
      const ok1 = options.axes.reversed && axis.ticks[key].isFirst;
      const ok2 = !options.axes.reversed && axis.ticks[key].isLast;
      if (ok1 || ok2) {
        const isVideoGroup = options.id === 'video' || options.id === 'twitterVideo'
        let label = axis.ticks[key].label.element;

        // To align the 0 properly with other labels without having
        // decimals, the decimals are made inivisible. But this means
        // any units suffix must be offset to not show up to the right
        // of these invisible decimals but instead right next to the 0.
        const tspan = label.querySelector('tspan');
        let xOffset = 0;
        if (isVideoGroup) {
          xOffset = label.getBoundingClientRect().width;
        }
        if (tspan && tspan.getAttribute('opacity') === '0') {
          let tspanInnerHTML = label.innerHTML + '';
          let labelFullWidth = label.getBoundingClientRect().width;
          label.innerHTML = '0';
          let labelTmpWidth = label.getBoundingClientRect().width;
          xOffset += labelTmpWidth - labelFullWidth;
          label.innerHTML = tspanInnerHTML;
        }
        const unitStr = options.product === 'financialnews' ? options.axes.units.suffix : options.axes.units.suffix.replace(/^\s+/, NBSP);
        if ((options.type === 'line' || options.type === 'column') && options.product === 'marketwatch') {
          label.innerHTML = label.innerHTML + '<tspan>' + ' ' + unitStr + '</tspan>';
          break;
        }
        if ((options.type === 'line' || options.type === 'column') && options.product === 'financialnews') {
          if (options.axes.units.suffix !== "%") {
            const labelYVal = label.getAttribute('y');
            const highChartContainer = highchart.container;
            let newLabelYVal = labelYVal - 7;
            styles.NORMAL_STYLE.fontSize = 10;
            styles.NORMAL_STYLE.color = '#555555';
            const firstYAxisLabel = highChartContainer.getElementsByClassName('highcharts-yaxis-labels')[0].lastElementChild.getBoundingClientRect().right;
            const suffix = renderer.text(unitStr, +label.getAttribute('x'), +newLabelYVal)
              .css(styles.NORMAL_STYLE)
              .add();
            suffix.element.setAttribute('class', 'units y-axis-suffix');
            const suffixRight = suffix.element.getBoundingClientRect().right;
            const currentSuffX = suffix.element.getAttribute('x');
            const diff = suffixRight - firstYAxisLabel;
            suffix.element.setAttribute('x', currentSuffX - diff);

            // suffix.element.setAttribute('x', currentSuffX - diff);
            break;
          } else {
            label.innerHTML = label.innerHTML + '<tspan>' + unitStr + '</tspan>';
            break;
          }

        } else {
          let css = styles.NORMAL_STYLE;

          if (__WSJ__) {
            if (options.product === 'wsj') {
              css = Object.assign({}, css, {
                fontSize: styles.AXES.label.fontSize,
                color: styles.AXES.label.color,
                fill: styles.AXES.label.color
              });
            }
          }

          if (__MANSIONGLOBAL__) {
            if (options.product === 'mansionglobal') {
              css = Object.assign({}, css, {
                color: styles.AXES.color,
                fill: styles.AXES.color
              });
            }
          }

          const suffix = renderer.text(unitStr, +label.getAttribute('x') + xOffset, +label.getAttribute('y'))
            .css(css)
            .add();
          const currentY = suffix.element.getAttribute('y');
          suffix.element.setAttribute('class', 'units y-axis-suffix');
          // aligns the suffix with the label
          if (!options.print) {
            suffix.element.setAttribute('y', +currentY + .5);
          }
          break;
        }
      }
    }
  }

}

// On vertical bar / column charts, insert a small '0' in place of bars that
// have a zero value, so that it does not look like an error.
function insertZeroesForEmptyBars(renderer, axis, options) {
  const typeOk = options.type === 'column';
  const stackOk = !options.stacking || axis.series.length === 1;
  if (typeOk && stackOk) {
    const series = axis.series;
    const allLessThanZero = allValuesLessThanZero(axis);
    let g = [];
    axis.series.forEach(series => {
      series.data.forEach(item => {
        if (item.y === 0) {
          const el = item.graphic.element;
          const translate = transform.read(el.parentElement, 'translate');
          if (translate) {
            let x = +el.attributes.x.value + translate[0];
            let y = +el.attributes.y.value + translate[1];
            let smallStyle = options.styles.get('small');
            if (options.product === 'wsj') {
              smallStyle.color = '#555555';
            } else {
              smallStyle.color = item.color;
            }
            x += +el.attributes.width.value / 2;
            y += allLessThanZero ? parseFloat(options.styles.get('small').fontSize) : -4;
            const text = renderer.text('0', x, y)
              .attr({ 'text-anchor': 'middle' })
              .css(smallStyle)
              .add();
            g.push({ type: '0', el: text.element });
          }
          
        } else if (item.graphic) {
          g.push({ type: 'bar', el: item.graphic.element });
        }
      });
    });

    // Sort by `left` position.
    g.sort(function (a, b) {
      return a.el.getBoundingClientRect().left -
        b.el.getBoundingClientRect().left;
    });

    // Check if 0s overlap bars or other 0s and, if so, remove.
    for (let i = 0; i < g.length - 1; i++) {
      if (g[i].type === '0') {
        const r1 = g[i].el.getBoundingClientRect();
        const r2 = g[i + 1].el.getBoundingClientRect();
        if (r1.right + 2 > r2.left) {
          element.removeChild(g[i].el);
        }
      }
    }
  }
}

// Returns true if all values are <= 0.
function allValuesLessThanZero(axis) {
  return axis.series.every(series => {
    return series.data.every(item => {
      return item.y <= 0;
    })
  });
}

// In Highcharts, grid lines ares places under lines and columns, which is
// fine. But we want the 0 line to be above (as in z or depth).
function moveZeroLineToTop(container, axis, options) {
  const zero = axis.ticks[0];
  if (zero) {
    const el = zero.gridLine.element;
    if (__WSJ__) {
      if (options.size === 'print-wsj') {
        const d = transform.read(el, 'd');
        if (options.type === 'bar') {
          d[0][0] = Math.floor(d[0][0]) - 0.5;
          d[1][0] = d[0][0];
        } else {
          d[0][1] = Math.ceil(d[0][1]);
          d[1][1] = d[0][1];
        }
        transform.write(el, 'd', d);
      }
    }
    container.querySelector('.highcharts-series-group').appendChild(el);
  }
}

// Sometimes Highcharts lets labels slip off the edge of chart, as if it
// measured the label width using a short label. Push the labels over so they
// all fit.
function ensureLabelsFit(axis, options, highchart) {
  // ensureHorizontalLabelsFit(axis);
  if (options.type === 'bar') {
    if (!options.axes.reversed) {
      ensureHorizontalLabelsFit(axis);
    }
  } else {
    if (!options.headings.yaxisTitle.text) {
      ensureVerticalLabelsFit(axis, options, highchart);
    }
  }
}

function ensureHorizontalLabelsFit(axis) {
  const key = axis.tickPositions[axis.tickPositions.length - 1];
  const tick = axis.ticks[key];
  const el = tick.label.element;
  const x2 = tick.label.xy.x + el.getBoundingClientRect().width / 2;
  const dif = axis.left + axis.width - x2;
  tick.label.xy.x += dif;
  if (dif < 0) {
    el.setAttribute('x', +el.getAttribute('x') + dif);
  }
}

function ensureVerticalLabelsFit(axis, options, highchart) {
  let maxWidth = 0;
  forAxisTicks(axis, tick => {
    const width = tick.label.element.getBoundingClientRect().width;
    maxWidth = Math.max(maxWidth, width);
  });  
  maxWidth = Math.ceil(maxWidth);
  if (options.product !== "marketwatch" && options.product !== 'financialnews') {
    forAxisTicks(axis, tick => {
      tick.label.element.setAttribute('x', maxWidth);
    });
  }
  else {
    if (options.type !== 'scatter') {
      if (__FINANCIALNEWS__) {
        if (options.product === 'financialnews') {
          if (options.type === 'line' || options.type === 'column') {
            const highChartContainer = highchart.container;
            let labelsG = highChartContainer.getElementsByClassName('highcharts-yaxis-labels');
            let translateValue = options.print === true ? 'translate(0, 5)' : 'translate(0, 6)';
            labelsG[0].attributes.transform.nodeValue = translateValue;
            const gElRectLabels = highChartContainer.getElementsByClassName('highcharts-yaxis-labels')[0];
            const gElRectRightLabels = gElRectLabels.getBoundingClientRect().right;
            const gElRectLabelsChildren = gElRectLabels.children;
            const horizontalGridLineArr = highChartContainer.getElementsByClassName('highcharts-yaxis-grid')[0].children;
            const gWidth = gElRectLabels.getBoundingClientRect().width;
            // if (options.type === 'line') {
            //   // translate the yaxis labels to the right based on width of surround g element
            //   gElRectLabels.setAttribute('transform', 'translate(' + gWidth + ',' + '6)' );
            //   for (let i = 0; i < gElRectLabelsChildren.length; i++) {
            //     gElRectLabelsChildren[i].setAttribute('text-anchor', 'end');
            //     // if there's a 0 in yaxis labels remove the extra space/zero
            //     const hasZero = gElRectLabels.children[i].innerHTML === '0' || gElRectLabels.children[i].innerHTML[1] === '<' ? true : false;
            //     if (hasZero) {
            //       gElRectLabels.children[i].children[0].innerHTML = '';
            //     }
            //     const currentXVal = Number(gElRectLabelsChildren[i].getAttribute('x'));
            //     const dValue = transform.read(horizontalGridLineArr[i], 'd');
            //     let lastHorizontalGridLineElD = horizontalGridLineArr[i].getAttribute('d').split(' ');
            //     const childWidth = gElRectLabelsChildren[i].getBoundingClientRect().width;
            //     // extend horizontal gridlines to match position of label, which is now right aligned. highcharts positions them to be left aligned and all horizontal gridlines end at some point, thereby having same length
            //     lastHorizontalGridLineElD[3] = ((currentXVal + (gWidth) - childWidth - 2)) + "," + dValue[1][1];
            //     // if first yaxis label has a percentage sign, decrease length of horizontal gridline to account for extra space that % creates
            //     lastHorizontalGridLineElD[3] = (options.axes.units.suffix === '%') && i === gElRectLabelsChildren.length - 1 ? ((currentXVal + (gWidth) - childWidth - 15)) + "," + dValue[1][1] : lastHorizontalGridLineElD[3];
            //     // make sure first gridline has proper length
            //     lastHorizontalGridLineElD[3] = options.axes.units.prefix !== "" && i === gElRectLabelsChildren.length - 1 ? (currentXVal - (gWidth * 0.125)) + "," + dValue[1][1] : lastHorizontalGridLineElD[3];
            //     lastHorizontalGridLineElD[2] = '';
            //     lastHorizontalGridLineElD = lastHorizontalGridLineElD.join(' ');
            //     horizontalGridLineArr[i].setAttribute('d', lastHorizontalGridLineElD);
            //   }
            // }
          }
          if (options.type !== 'line' && options.type !== 'column') {
            forAxisTicks(axis, tick => {
              tick.label.element.setAttribute('x', maxWidth);
            });
          }
        }
      }
      if (__MARKETWATCH__) {
        if (options.type === 'line' && options.product === 'marketwatch') {
          const highChartContainer = highchart.container;
  
          let labelsG = highChartContainer.getElementsByClassName('highcharts-yaxis-labels');
          let translateValue = 'translate(0, 6)';
          labelsG[0].attributes.transform.nodeValue = translateValue;
        }
        if (options.type === 'column' && options.product === 'marketwatch') {
          const highChartContainer = highchart.container;
          let labelsG = highChartContainer.getElementsByClassName('highcharts-yaxis-labels');
          let translateValue = 'translate(0, 6)';
          labelsG[0].attributes.transform.nodeValue = translateValue;
        }
  
        if (options.type !== "line" && options.type !== 'column') {
          forAxisTicks(axis, tick => {
            tick.label.element.setAttribute('x', maxWidth);
          });
        }
      }
    }
    
  }

}

function shiftLastHorizontalBarLine(highchart, options) {
  const axis = highchart.axes[1];
  forAxisTicksReverse(axis, tick => {
    if (tick.isLast) {
      const d = transform.read(tick.gridLine.element, 'd');
      d[0][0] -= 1;
      d[1][0] = d[0][0];
      transform.write(tick.gridLine.element, 'd', d);
      // break:
      return false;
    }
  });
}

function moveTopLabel(axis, options){
  const isColumn = options.type === 'column';
  const isPrint = options.size === 'print-wsj'
  const is8u = options.size === '8u';

  for (let tick in axis.ticks) {
    if(axis.ticks[tick].isLast){
      const topTick = axis.ticks[tick];
      const label = axis.ticks[tick].label.element;
      const position = +label.getAttribute('y');
      let offset = -2;

      if(is8u){
        offset = -1;
      }

      if(isColumn){
        offset = -1;
      }

      if(isPrint){
        offset = -.5;
      }

      const newPostition = position + offset;
      label.setAttribute('y', newPostition);
    }
  }
}


// WEBPACK FOOTER //
// ./src/chart/inner/axes-postrender-y-axis.js